import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { offlineMessaging } from '../utils/offlineMessaging';

export interface OfflineMessage {
  id: string;
  sender_id: string;
  receiver_id: string;
  message: string;
  created_at: string;
  read_at?: string;
  edited_at?: string;
  is_deleted: boolean;
  synced: boolean;
  retry_count: number;
}

export const useOfflineMessaging = (currentUserId: string) => {
  const [messages, setMessages] = useState<OfflineMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load messages from offline storage
  const loadOfflineMessages = useCallback(async () => {
    if (!currentUserId) return;
    
    try {
      const offlineMessages = await offlineMessaging.getMessages(currentUserId);
      setMessages(offlineMessages);
      
      const unread = await offlineMessaging.getUnreadCount(currentUserId);
      setUnreadCount(unread);
    } catch (error) {
      console.error('Failed to load offline messages:', error);
    }
  }, [currentUserId]);

  // Sync with server when online - disabled to prevent timeout errors
  const syncMessages = useCallback(async () => {
    // Disabled automatic syncing to prevent timeout errors
    return;
  }, []);

  // Send message (works offline)
  const sendMessage = useCallback(async (receiverId: string, messageText: string) => {
    if (!currentUserId || !messageText.trim()) return null;

    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const message: OfflineMessage = {
      id: messageId,
      sender_id: currentUserId,
      receiver_id: receiverId,
      message: messageText.trim(),
      created_at: new Date().toISOString(),
      is_deleted: false,
      synced: false,
      retry_count: 0
    };

    try {
      // Store locally first
      await offlineMessaging.storeMessage(message);
      
      if (isOnline) {
        // Try to send to server immediately
        try {
          const { data, error } = await supabase
            .from('messages')
            .insert({
              sender_id: message.sender_id,
              receiver_id: message.receiver_id,
              message: message.message
            })
            .select()
            .single();

          if (error) throw error;

          // Update with server ID and mark as synced
          const syncedMessage = { ...message, id: data.id, synced: true };
          await offlineMessaging.storeMessage(syncedMessage);
        } catch (error) {
          // Queue for later sync
          await offlineMessaging.queueOperation({
            id: `send_${messageId}`,
            action: 'send',
            data: {
              sender_id: message.sender_id,
              receiver_id: message.receiver_id,
              message: message.message
            },
            timestamp: new Date().toISOString(),
            retry_count: 0
          });
        }
      } else {
        // Queue for when online
        await offlineMessaging.queueOperation({
          id: `send_${messageId}`,
          action: 'send',
          data: {
            sender_id: message.sender_id,
            receiver_id: message.receiver_id,
            message: message.message
          },
          timestamp: new Date().toISOString(),
          retry_count: 0
        });
      }

      await loadOfflineMessages();
      return message;
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  }, [currentUserId, isOnline, loadOfflineMessages]);

  // Mark messages as read
  const markAsRead = useCallback(async (senderId: string) => {
    if (!currentUserId) return;

    try {
      const readTime = new Date().toISOString();
      
      // Update offline storage
      const allMessages = await offlineMessaging.getMessages(currentUserId);
      const unreadMessages = allMessages.filter(msg => 
        msg.sender_id === senderId && 
        msg.receiver_id === currentUserId && 
        !msg.read_at
      );

      for (const msg of unreadMessages) {
        const updatedMsg = { ...msg, read_at: readTime };
        await offlineMessaging.storeMessage(updatedMsg);
      }

      await loadOfflineMessages();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  }, [currentUserId, loadOfflineMessages]);

  // Edit message
  const editMessage = useCallback(async (messageId: string, newText: string) => {
    if (!currentUserId || !newText.trim()) return;

    try {
      // Update offline storage
      const allMessages = await offlineMessaging.getMessages(currentUserId);
      const message = allMessages.find(m => m.id === messageId);
      
      if (message && message.sender_id === currentUserId) {
        const updatedMsg = { 
          ...message, 
          message: newText.trim(),
          edited_at: new Date().toISOString()
        };
        await offlineMessaging.storeMessage(updatedMsg);
        await loadOfflineMessages();
      }
    } catch (error) {
      console.error('Failed to edit message:', error);
    }
  }, [currentUserId, loadOfflineMessages]);

  // Delete message permanently
  const deleteMessage = useCallback(async (messageId: string) => {
    if (!currentUserId) return;

    try {
      // Delete from database if online
      if (isOnline) {
        const { error } = await supabase
          .from('messages')
          .delete()
          .eq('id', messageId)
          .eq('sender_id', currentUserId);
        
        if (error) {
          console.error('Database delete error:', error);
        }
      }
      
      // Remove from offline storage completely
      await offlineMessaging.deleteMessage(messageId);
      await loadOfflineMessages();
    } catch (error) {
      console.error('Failed to delete message:', error);
      throw error;
    }
  }, [currentUserId, isOnline, loadOfflineMessages]);

  // Initial load only - no syncing
  useEffect(() => {
    if (currentUserId) {
      // Clear any existing offline data on first load
      offlineMessaging.clearAll().then(() => {
        loadOfflineMessages();
      });
    }
  }, [currentUserId, loadOfflineMessages]);

  return {
    messages: messages.filter(m => !m.is_deleted),
    unreadCount,
    isOnline,
    syncing,
    sendMessage,
    markAsRead,
    editMessage,
    deleteMessage,
    syncMessages,
    loadOfflineMessages
  };
};